import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorKlikker() {
  return (
    <ArticleLayout
      title="Radiatoren klikker: Varmeudvidelse, rørgennemføringer og nattelyde"
      metaTitle="Radiator klikker eller tikker? Årsager og løsninger"
      metaDescription="Klikker radiatoren om natten eller når den tænder og slukker? Lær hvordan varmeudvidelse i metalrør og stramme gulvbøsninger skaber kliklydene."
      canonicalPath="/radiator-klikker"
      introLead="En klikkende, tikkende eller knirkende radiator skyldes næsten altid termisk udvidelse i varmerørene og radiatorkroppen."
      introAnswer="Når varmt vand løber ind i kolde rør, udvider stålet eller kobberet sig med brøkdele af millimeter. Hvis rørene gnider mod bjælkelag, trægulve eller tørre vægbeslag, udløses spændingen i små metalliske ryk, som opfattes som klikkelyde – særligt i nattetimerne, når varmeanlægget regulerer."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Sådan dæmper du klikkelydene' },
        { id: 'hvorfor-klik', title: '2. Fysikken bag: Termisk friktion' },
        { id: 'natten', title: '3. Hvorfor klikker radiatoren om natten?' },
        { id: 'beslag', title: '4. Bæreknægte og rørgennemføringer' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 trin til at standse klikkelyde"
      checklist={[
        {
          title: 'Hold radiatoren på en stabil, konstant temperatur',
          description: 'Undgå at skrue op og ned hele tiden; en jævn temperatur minimerer metallets konstante udvidelse og sammentrækning.',
        },
        {
          title: 'Tjek radiatorens bæreknægte på væggen for plastikbøsninger',
          description: 'Mellem radiatorkroppen og vægbeslaget skal der sidde små plastikstykker for at forhindre stål-mod-stål friktion.',
        },
        {
          title: 'Smør kontaktfladerne på vægbeslagene med tør silikonespray',
          description: 'Et par dråber silikonespray eller PTFE-smøremiddel på bæreknægtene fjerner knirken fra varmebevægelser.',
        },
        {
          title: 'Undersøg rørgennemføringen i gulv og etageadskillelse',
          description: 'Røret må ikke røre direkte ved gulvbrædder; der skal være luft eller en blød mineralulds- eller filtforing.',
        },
        {
          title: 'Afmontér dækrosetter og tjek for fri bevægelighed',
          description: 'Løft plastik- eller trækappen ved gulvet og kontrollér, at røret ikke er kilet fast mod spånplade eller parket.',
        },
      ]}
      sections={[
        {
          id: 'hvorfor-klik',
          title: 'Fysikken bag: Termisk udvidelse og friktion',
          content: (
            <>
              <p>
                Metaller som stål, jern og kobber udvider sig, når de varmes op. Et 10 meter langt stålrør udvider sig med op mod <span className="font-mono">5–7 mm</span>, når vandtemperaturen stiger fra 20 °C til 65 °C.
              </p>
              <p>
                Røret skal kunne glide frit i sine ophæng under denne bevægelse. Hvis en rørføring sidder i klemme i en træbjælke eller i en bæreknægt uden glidebøsning, opbygges der mekanisk spænding. Når spændingen overstiger friktionen, slipper røret i et mikroskopisk ryk, som forplanter sig som et markant klik i hele radiatorkroppen.
              </p>
            </>
          ),
        },
        {
          id: 'natten',
          title: 'Hvorfor klikker radiatoren særligt om natten og når den er slukket?',
          content: (
            <>
              <p>
                Mange boligejere og lejere oplever klikkelydene i de sene aftentimer eller midt om natten. Det sker typisk af to årsager:
              </p>
              <p>
                <strong>Automatisk natsænkning:</strong> Hvis varmecentralen eller din egen styring sænker fremløbstemperaturen om natten, begynder hele rørsystemet at køle af og trække sig sammen. Sammentrækningen giver nøjagtig samme klikkende friktion som opvarmningen.
              </p>
              <p>
                <strong>Rumtermostatens åbne/lukke-cyklus:</strong> Når temperaturen i soveværelset når setpunktet, lukker ventilen. Radiatoren køler af, og klikkelyden opstår. Lidt efter falder temperaturen, ventilen åbner igen, hedt vand strømmer ind, og et nyt sæt klik høres.
              </p>
            </>
          ),
        },
        {
          id: 'beslag',
          title: 'Løsning ved vægbeslag og gulvbøsninger',
          content: (
            <>
              <p>
                De to mest gængse fejlkilder er lette at afhjælpe uden at skille rør ad:
              </p>
              <p>
                <strong>Bæreknægte:</strong> Løft radiatoren en halv millimeter op (eller aflast den let med en kile under bunden) og kontrollér, om de små hvide plastikindlæg på vægbeslagene mangler eller er slidt igennem. Mangler de, kan man erstatte dem eller spraye med silikone.
              </p>
              <p>
                <strong>Gulvhuller:</strong> Ved gulvgennemføringen saves der ofte et for stramt hul ved montering af trægulve. Udvid forsigtigt hullet omkring røret med et stemmejern eller en rund rasp, så røret har mindst 2 mm frirum hele vejen rundt, og afslut med en fleksibel dækroset.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår kræver klikkelyde en VVS-fagmand?',
        content: (
          <p>
            Hvis klikkelydene stammer fra rør, der er støbt fast i beton eller indmuret uden ekspansionsrør, kan man ikke selv løse problemet uden bygningsmæssige indgreb. En VVS-installatør kan vurdere, om der kan monteres en kompensator (ekspansionsbælg) på strengen, eller om fremløbstemperaturen kan sænkes for at reducere spændingerne.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Er det farligt, at radiatoren klikker?',
          a: 'Nej, klikkelyde er ikke tegn på eksplosionsfare eller akut lækage. Det er et akustisk irritationsmoment forårsaget af helt naturlig metaludvidelse.',
        },
        {
          q: 'Hvorfor klikker radiatoren, når den er slukket?',
          a: 'Selvom termostaten er lukket, kan varmerørene bagved stadig transportere varmt vand videre til andre radiatorer i huset, og temperaturudsving i disse rør forplanter sig.',
        },
        {
          q: 'Kan udluftning fjerne klikkelyde?',
          a: 'Nej, udluftning fjerner kun risle- og klukkelyde forårsaget af luftbobler. Klikkelyde er mekanisk friktion og påvirkes ikke af luftskruen.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-larmer', text: 'Radiatoren larmer, suser eller banker', desc: 'oversigt over alle radiatorens lyde' },
        { to: '/radiator-lyder-som-vand', text: 'Radiator lyder som vand', desc: 'rislen og klukken fra luftlommer' },
        { to: '/radiator-banker-fjernvarme', text: 'Radiator banker ved fjernvarme', desc: 'kraftige trykslag og ventilfejl' },
        { to: '/udluftning-af-radiator', text: 'Sådan lufter du radiatoren ud', desc: 'vejledning med radiatornøgle' },
      ]}
      sources={[
        { url: 'https://www.danfoss.com/da-dk/industries/buildings-residential/dhs/solutions-for-homeowners/saving-energy/', label: 'Gode råd til at spare på varmen | Danfoss' },
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
      ]}
    />
  );
}
